/**
 * El aviso de «te pasas de tu plan».
 *
 * Aparece cuando un sitio tiene más de lo que su plan incluye, que en la
 * práctica siempre significa lo mismo: alguien bajó de plan y conserva lo que
 * hizo cuando pagaba más. Nunca borramos nada por eso —15 páginas siguen
 * publicadas y editándose—, así que este aviso no anuncia una pérdida: explica
 * por qué el botón de crear dejó de responder.
 *
 * Va arriba del todo, como el de disputas, porque no pertenece a ninguna
 * sección: el tope afecta a las páginas, al CMS y a los contactos por igual, y
 * ponerlo dentro de una de ellas lo escondería de las otras dos.
 *
 * No se puede cerrar, y no hace falta: desaparece solo en cuanto suben de plan
 * o bajan de la línea. Un aviso que se va cuando el hecho que lo causa deja de
 * ser cierto no necesita botón de descarte.
 *
 * Cuando hay una disputa abierta, ésta gana y el aviso de plan espera: un plazo
 * que corre urge más que un tope, y dos bandas apiladas empujan el panel entero
 * hacia abajo. Lo resuelve la hoja de estilos con un selector hermano, sin que
 * los dos componentes tengan que conocerse.
 */
import { IconAlertTriangleFilled } from '@tabler/icons-react'
import { PLAN_LABEL, countLabel } from '../../lib/planLimits'
import type { Overage, Plan } from '@webbuilder/plans'
import { useI18n } from '../../lib/i18n'

export function PlanOverageBanner({
  plan,
  overages,
  onOpenPlans,
}: {
  plan: Plan
  overages: Overage[]
  onOpenPlans: () => void
}) {
  const { t } = useI18n()
  if (overages.length === 0) return null

  /* «15 páginas de 3» y no «tu plan permite 3»: lo primero que hay que ver es
     cuánto hay de más, que es la cifra que decide si se sube de plan o se
     recorta. */
  const details = overages
    .map((overage) => t('plans.over.detail', {
      count: countLabel(overage.resource, overage.used, t),
      limit: overage.limit,
    }))
  const detail = details.join(' · ')

  return (
    <div className="dash-dispute-banner dash-plan-banner" role="status">
      <p className="dash-dispute-banner-inner">
        <span className="dash-dispute-banner-icon" aria-hidden="true">
          <IconAlertTriangleFilled size={16} />
        </span>

        <strong className="dash-plan-banner-title-desktop">{t('plans.over.title', { plan: PLAN_LABEL[plan] })}</strong>
        <strong className="dash-plan-banner-title-mobile">{t('plans.over.mobileTitle')}</strong>

        <span className="dash-dispute-banner-sep" aria-hidden="true">·</span>

        <span className="dash-dispute-banner-detail">
          <span className="dash-plan-banner-detail-desktop">{detail}. {t('plans.over.body')}{' '}</span>
          <span className="dash-plan-banner-detail-mobile">
            {details[0] ?? detail}
            {details.length > 1 ? ` · ${t('plans.over.more', { count: details.length - 1 })}` : ''}
          </span>
          <button type="button" className="dash-dispute-banner-link" onClick={onOpenPlans}>
            {t('plans.over.seePlans')}
          </button>
        </span>
      </p>
    </div>
  )
}
